/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { When } from "react-if";
import { theme } from "@nordcloud/gnui";
import {
  DataContainer,
  DataItem,
  DateRange,
  EstatePeriod,
  Separator,
} from "~/components";
import { useCurrency } from "~/hooks";
import { formatMoney, isNotNil } from "~/tools";
import { PeriodSelection } from "../Views";
import { EstateGroupBy } from "./types";

type Props = {
  period: EstatePeriod;
  range: DateRange | undefined;
  chartType: EstateGroupBy;
  totalTop: string;
  totalOther: string;
  count: number;
  isLoading?: boolean;
};

export function TotalCostTable({
  period,
  range,
  chartType,
  totalTop,
  totalOther,
  count,
  isLoading = false,
}: Props) {
  const { currency } = useCurrency();

  const label = mapLabel(chartType);
  const totalCost = Number(totalTop) + Number(totalOther);

  return (
    <>
      <PeriodSelection period={period} range={range} />
      <Separator marginBottom={theme.spacing.spacing04} />
      <DataContainer width="100%">
        <DataItem
          label="Total Cost"
          value={formatMoney(totalCost, currency)}
          grow={false}
          loading={isLoading}
          valueFontWeight={theme.fontWeights.medium}
          itemWidth="20%"
        />
        <When condition={showTop10Fields(chartType)}>
          <DataItem
            label={`Top 10 Spending ${label}`}
            value={isNotNil(totalTop) ? formatMoney(totalTop, currency) : "-"}
            grow={false}
            loading={isLoading}
            valueFontWeight={theme.fontWeights.medium}
            itemWidth="20%"
          />
          <DataItem
            label={`All other ${label}`}
            grow={false}
            valueFontWeight={theme.fontWeights.medium}
            loading={isLoading}
            itemWidth="20%"
            value={
              isNotNil(totalOther) ? formatMoney(totalOther, currency) : "-"
            }
          />
        </When>
        <DataItem
          label={`Total ${label}`}
          grow={false}
          valueFontWeight={theme.fontWeights.medium}
          loading={isLoading}
          itemWidth="20%"
          value={count?.toString()}
        />
      </DataContainer>
    </>
  );
}

// Map other options here when implemented
function mapLabel(chartType: EstateGroupBy) {
  if (chartType === EstateGroupBy.APP) {
    return "Applications";
  }
  if (chartType === EstateGroupBy.PROVIDER) {
    return "Providers";
  }
  if (chartType === EstateGroupBy.ENV) {
    return "Environment types";
  }
  if (chartType === EstateGroupBy.CATEGORY) {
    return "Categories";
  }
  if (chartType === EstateGroupBy.REGION) {
    return "Regions";
  }
  if (chartType === EstateGroupBy.ACCOUNT) {
    return "Cloud Accounts";
  }
  return "";
}

function showTop10Fields(chartType: EstateGroupBy) {
  return [
    EstateGroupBy.APP,
    EstateGroupBy.REGION,
    EstateGroupBy.CATEGORY,
    EstateGroupBy.ACCOUNT,
  ].includes(chartType);
}
